import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import { makeColorFn } from "../lib/colorScales";

const sampleGrid = (grid, hi, lat, lon) => {
  const { lats, lons, nx, ny, values } = grid;
  const fx = ((lon - lons[0]) / (lons[nx - 1] - lons[0])) * (nx - 1);
  const fy = ((lat - lats[0]) / (lats[ny - 1] - lats[0])) * (ny - 1);
  if (!(fx >= 0 && fy >= 0 && fx <= nx - 1 && fy <= ny - 1)) return NaN;
  const x0 = Math.floor(fx), y0 = Math.floor(fy);
  const x1 = Math.min(x0 + 1, nx - 1), y1 = Math.min(y0 + 1, ny - 1);
  const tx = fx - x0, ty = fy - y0;
  const g = (xi, yi) => values[yi * nx + xi][hi] ?? NaN;
  return (g(x0, y0) * (1 - tx) + g(x1, y0) * tx) * (1 - ty) + (g(x0, y1) * (1 - tx) + g(x1, y1) * tx) * ty;
};

// marching squares: returns list of [x1,y1,x2,y2] in sample coords
const contours = (F, sw, sh, level) => {
  const segs = [];
  const lerp = (a, b, va, vb) => a + ((level - va) / (vb - va || 1e-9)) * (b - a);
  for (let y = 0; y < sh - 1; y++) {
    for (let x = 0; x < sw - 1; x++) {
      const tl = F[y][x], tr = F[y][x + 1], br = F[y + 1][x + 1], bl = F[y + 1][x];
      if (Number.isNaN(tl) || Number.isNaN(tr) || Number.isNaN(br) || Number.isNaN(bl)) continue;
      const idx = (tl >= level ? 8 : 0) | (tr >= level ? 4 : 0) | (br >= level ? 2 : 0) | (bl >= level ? 1 : 0);
      if (idx === 0 || idx === 15) continue;
      const top = [lerp(x, x + 1, tl, tr), y];
      const right = [x + 1, lerp(y, y + 1, tr, br)];
      const bottom = [lerp(x, x + 1, bl, br), y + 1];
      const left = [x, lerp(y, y + 1, tl, bl)];
      const push = (a, b) => segs.push([a[0], a[1], b[0], b[1]]);
      switch (idx) {
        case 1: case 14: push(left, bottom); break;
        case 2: case 13: push(bottom, right); break;
        case 3: case 12: push(left, right); break;
        case 4: case 11: push(top, right); break;
        case 5: push(top, left); push(bottom, right); break;
        case 6: case 9: push(top, bottom); break;
        case 7: case 8: push(top, left); break;
        case 10: push(top, right); push(left, bottom); break;
        default: break;
      }
    }
  }
  return segs;
};

export const ScalarLayer = ({ grid, variable, ts, theme, enabled }) => {
  const map = useMap();
  const st = useRef({});
  const drawRef = useRef(() => {});

  useEffect(() => {
    st.current = { grid, variable, ts, theme, enabled };
    drawRef.current();
  }, [grid, variable, ts, theme, enabled]);

  useEffect(() => {
    const container = map.getContainer();
    const canvas = document.createElement("canvas");
    canvas.className = "scalar-canvas";
    container.appendChild(canvas);
    const ctx = canvas.getContext("2d");
    const off = document.createElement("canvas");
    const octx = off.getContext("2d");

    const draw = () => {
      const { grid: g, variable: v, ts: t, theme: th, enabled: on } = st.current;
      const size = map.getSize();
      const w = size.x, h = size.y;
      canvas.width = w; canvas.height = h;
      ctx.clearRect(0, 0, w, h);
      if (!on || !g || g.var !== v) return;
      const hi = Math.min(g.hours - 1, Math.max(0, Math.round((t - g.t0) / g.step)));
      const scale = 4;
      const sw = Math.ceil(w / scale), sh = Math.ceil(h / scale);
      const lats = Array.from({ length: sh }, (_, y) => map.containerPointToLatLng([0, y * scale]).lat);
      const lons = Array.from({ length: sw }, (_, x) => map.containerPointToLatLng([x * scale, 0]).lng);
      const color = makeColorFn(v, g);
      const F = [];
      off.width = sw; off.height = sh;
      const img = octx.createImageData(sw, sh);
      const px = img.data;
      for (let y = 0; y < sh; y++) {
        const row = new Array(sw);
        for (let x = 0; x < sw; x++) {
          const val = sampleGrid(g, hi, lats[y], lons[x]);
          row[x] = val;
          const o = (y * sw + x) * 4;
          if (Number.isNaN(val)) { px[o + 3] = 0; continue; }
          const [r, gg, b] = color(val);
          px[o] = r; px[o + 1] = gg; px[o + 2] = b; px[o + 3] = 140;
        }
        F.push(row);
      }
      octx.putImageData(img, 0, 0);
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(off, 0, 0, w, h);

      if (v !== "pressure") return;
      const interval = map.getZoom() < 5 ? 4 : 2;
      const lo = Math.ceil(g.min / interval) * interval;
      ctx.lineWidth = 1;
      ctx.strokeStyle = th === "dark" ? "rgba(255,255,255,0.75)" : "rgba(15,23,42,0.75)";
      ctx.font = "600 10px JetBrains Mono, monospace";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      for (let level = lo; level <= g.max; level += interval) {
        const segs = contours(F, sw, sh, level);
        if (!segs.length) continue;
        ctx.beginPath();
        segs.forEach(([x1, y1, x2, y2]) => {
          ctx.moveTo(x1 * scale, y1 * scale);
          ctx.lineTo(x2 * scale, y2 * scale);
        });
        ctx.stroke();
        for (let i = 20; i < segs.length; i += 70) {
          const [x1, y1, x2, y2] = segs[i];
          const cx = ((x1 + x2) / 2) * scale, cy = ((y1 + y2) / 2) * scale;
          ctx.lineWidth = 3;
          ctx.strokeStyle = th === "dark" ? "rgba(2,6,23,0.85)" : "rgba(255,255,255,0.9)";
          ctx.strokeText(String(level), cx, cy);
          ctx.fillStyle = th === "dark" ? "#fff" : "#0f172a";
          ctx.fillText(String(level), cx, cy);
          ctx.lineWidth = 1;
          ctx.strokeStyle = th === "dark" ? "rgba(255,255,255,0.75)" : "rgba(15,23,42,0.75)";
        }
      }
    };
    drawRef.current = draw;
    const hide = () => { canvas.style.opacity = "0"; };
    const show = () => { draw(); canvas.style.opacity = "1"; };
    map.on("movestart zoomstart", hide);
    map.on("moveend zoomend resize", show);
    draw();
    return () => {
      map.off("movestart zoomstart", hide);
      map.off("moveend zoomend resize", show);
      canvas.remove();
    };
  }, [map]);

  return null;
};
